'use client';

import type { Ref } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { useUiContent } from '@/components/ui-content-provider';
import { replacePlaceholders } from '@/lib/ui-content/price-ui-content';
import { AffiliateInfoIcon } from '../affiliate-info-icon';
import { OfferRow } from './offer-row';
import {
  EXPANDER_H,
  FOOTER_H,
  HEADER_H,
  MAX_VISIBLE_OTHER_ROWS,
  formatObservedAt,
  type PreparedOffer,
} from './offers';

type OffersPanelProps = {
  /** The FULL prepared list. The panel does the slicing. */
  rows: PreparedOffer[];
  /** `prepareOffers().primary.domainKey`. That offer is NOT rendered as a row:
   *  it is the ink pick line at the foot of the panel (pick-line.tsx), which on
   *  desktop is the buy button itself, morphed in place. Listing it here as
   *  well would put the same retailer on screen twice. */
  primaryDomainKey?: string;
  /** `prepareOffers().oldestObservedAt` — NOT the freshest. The "prices
   *  checked" claim has to hold for every row on screen, so it is bounded by
   *  the stalest one (over the non-info rows, on the source's own timestamp). */
  oldestObservedAt: string | null;
  localeDetails: LocaleDetails;
  productTitle: string;
  index: number;
  categoryId?: string;
  asin?: string;
  hrefFor: (offer: PreparedOffer) => string;
  expanded: boolean;
  onExpand: () => void;
  onBack: () => void;
  onRowClick?: (offer: PreparedOffer) => void;
  backButtonRef?: Ref<HTMLButtonElement>;
  /** True once this panel has actually been on screen. Gates the retailer icon
   *  requests: the morph's panel face is in the SSR markup of every card, so
   *  without this every product page would fetch a favicon per retailer for a
   *  panel most readers never open. */
  active?: boolean;
  /** 'morph' fills the height `panelHeight()` computed for exactly these rows;
   *  'sheet' grows to its content and lets the bottom sheet scroll. Also
   *  decides who owns the dialog semantics — the sheet element already carries
   *  them. Neither variant ever scrolls the row list itself. */
  variant: 'morph' | 'sheet';
};

/**
 * The offers list itself — shared verbatim between the desktop morph face and
 * the mobile bottom sheet, so the ordering, the badges and the freshness claim
 * can only be wrong in one place.
 *
 * ONE GUTTER, 10px. Every leading edge in here — the Back chevron, each row's
 * retailer mark, the "Show all {n}" label, the disclosure icon — sits exactly
 * 10px from the panel's own edge, and every trailing edge (the title, each
 * row's chevron) sits 10px from the other side. The hover surfaces stop 4px
 * short of it, so a hovered row reads as a card floating inside the gutter
 * rather than as a second, competing alignment. That is why the 10 is split
 * between the container (`px-1` = 4) and the hoverable child (`px-1.5` = 6)
 * instead of being spent in one place — change one and you owe the other.
 *
 * Both surfaces are light-on-light / dark-on-dark (`bg-light dark:bg-dark` for
 * the morph, `bg-white/95 dark:bg-dark/95` for the sheet), so ordinary `dark:`
 * variants are correct here — there is no inverted-surface case to special-case.
 */
export function OffersPanel({
  rows,
  primaryDomainKey,
  oldestObservedAt,
  localeDetails,
  productTitle,
  index,
  categoryId,
  asin,
  hrefFor,
  expanded,
  onExpand,
  onBack,
  onRowClick,
  backButtonRef,
  variant,
  active = true,
}: OffersPanelProps) {
  const ui = useUiContent();

  // The primary is lifted out of the list and pinned to the foot as the pick
  // line, so what is listed here is everything ELSE we track in this market.
  // The original position travels with each row: `offer_index` has to keep
  // meaning "position in the full prepared list", or a panel's click data stops
  // being comparable with the pill's.
  const others = rows
    .map((offer, position) => ({ offer, position }))
    .filter(({ offer }) => offer.domainKey !== primaryDomainKey);
  const visibleRows = expanded ? others : others.slice(0, MAX_VISIBLE_OTHER_ROWS);
  const showExpander = !expanded && others.length > MAX_VISIBLE_OTHER_ROWS;

  const title = ui.chooseWhereToBuy ?? 'Where to buy';
  const observed = formatObservedAt(oldestObservedAt, localeDetails.languageCode);
  const freshness = observed
    ? replacePlaceholders(ui.pricesChecked ?? 'Prices checked {time}', { time: observed })
    : '';

  // What the prices are (the best price we observed, the store confirms at
  // checkout) and Amazon's price disclaimer are no hidden text here any more
  // (2026-10-03): both sit in the disclosure's info icon (OffersDisclosure
  // below, offersDisclosureNotes), visible on tap or hover and read as the
  // icon's name, so sighted readers, screen readers and crawlers get the
  // same words.

  return (
    <div
      className={`flex flex-col ${variant === 'morph' ? 'h-full' : ''}`}
      // The sheet element is already role="dialog" aria-modal; nesting a second
      // dialog inside it would make screen readers announce two.
      {...(variant === 'morph' ? { role: 'dialog', 'aria-label': title } : {})}
    >
      <div
        className="flex flex-shrink-0 items-center gap-1 border-b border-black/[0.07] px-1 dark:border-white/10"
        style={{ height: HEADER_H }}
      >
        <button
          ref={backButtonRef}
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 rounded-lg px-1.5 py-1.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-black/[0.05] dark:text-zinc-200 dark:hover:bg-white/10"
        >
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 6-6 6 6 6" />
          </svg>
          {/* Required key, hand-set in all 24 core languages — never fall back. */}
          {ui.goBack}
        </button>
        {/* Two lines inside the same 44px strip, so the freshness claim becomes
            visible text without costing the panel a pixel. It is the cheapest
            piece of evidence we have that the comparison is real and recent,
            and until now it only existed for screen readers. */}
        <span className="flex min-w-0 flex-1 flex-col items-end pr-1.5">
          <span className="w-full truncate text-right text-sm font-semibold leading-tight text-zinc-800 dark:text-zinc-100">
            {title}
          </span>
          {freshness && (
            // Relative ("3 hours ago"), so the cached server render and the
            // hydrating client can word it differently; the element itself
            // depends only on whether a timestamp exists.
            <span
              suppressHydrationWarning
              className="w-full truncate text-right text-[0.62rem] leading-tight text-zinc-400 dark:text-zinc-500"
            >
              {freshness}
            </span>
          )}
        </span>
      </div>

      {/* ABOVE the rows, not below them. The list now grows upward off the pick
          line, so this is the edge new rows arrive at — and putting it under a
          bottom-aligned list would leave it floating in the panel's empty top
          half. */}
      {showExpander && (
        <button
          type="button"
          onClick={onExpand}
          style={variant === 'morph' ? { height: EXPANDER_H } : undefined}
          className={`flex flex-shrink-0 items-center gap-1 border-b border-black/[0.05] px-2.5 text-[0.72rem] font-medium text-cornflowerblue transition-colors hover:bg-cornflowerblue/[0.06] dark:border-white/10 ${
            variant === 'sheet' ? 'py-3' : ''
          }`}
        >
          {replacePlaceholders(ui.showAllOffers ?? 'Show all {n}', { n: String(rows.length) })}
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            className="h-3 w-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m18 15-6-6-6 6" />
          </svg>
        </button>
      )}

      {/* `panelHeight()` sizes the morph to exactly these rows, so this port is
          inert in every ordinary case — a container that is not overflowing
          swallows no trackpad scroll. It exists only for the one case that CAN
          overflow now: an expanded list long enough to hit `--offers-max-h`.
          The sheet still scrolls as one piece and never needs it.

          `justify-end` so the list grows UPWARD off the pick line: two
          retailers sit right above the button, and "Show all" adds rows at the
          top rather than pushing the recommended line away from the cursor. */}
      <div
        className={`flex flex-col px-1 ${
          variant === 'morph' ? 'min-h-0 flex-1 justify-end overflow-y-auto overscroll-contain' : ''
        }`}
      >
        {visibleRows.map(({ offer, position }) => (
          <OfferRow
            key={offer.domainKey}
            offer={offer}
            offerIndex={position}
            localeDetails={localeDetails}
            productTitle={productTitle}
            index={index}
            categoryId={categoryId}
            asin={asin}
            href={hrefFor(offer)}
            onClick={() => onRowClick?.(offer)}
            active={active}
          />
        ))}
      </div>

      {/* The disclosure is NOT rendered here by either surface. It belongs
          BELOW the pick line, and the pick line is the caller's: the morph puts
          it in the elastic strip the price block left behind
          (`.offers-morph-face-foot`), the sheet stacks it underneath. */}
    </div>
  );
}

/**
 * The affiliate disclosure strip.
 *
 * `affiliateDisclosure` is a full paragraph and this strip is one line, so what
 * used to happen here was that a five-line legal notice got line-clamped to two
 * and read as a truncated sentence. The visible line is a dedicated one-liner
 * sized for the strip; the complete disclosure still lives behind the icon
 * (portal tooltip) and on this paragraph's `title`, so nothing is lost — it is
 * just no longer pretending to fit. Up to two lines at 11px (fixed pixels,
 * because the root font-size scales with the viewport and 0.6rem came out near
 * 9px): one truncated line cut the English sentence at "It never af...", and
 * two lines at 1.2 leading still fit `FOOTER_H` and the morph's minimum gap.
 *
 * `fill` makes it stretch to `--offers-gap` inside the morph's foot rather than
 * sitting at the constant `FOOTER_H` the sheet still uses.
 */
export function OffersDisclosure({
  languageCode,
  fill = false,
  notes,
}: {
  languageCode: LocaleDetails['languageCode'];
  fill?: boolean;
  /** offersDisclosureNotes(): what the prices are and, when the panel lists
   *  an Amazon price, Amazon's disclaimer (spec 6.1.4), in full in the info
   *  icon's tooltip and accessible name. */
  notes?: ReadonlyArray<string>;
}) {
  const ui = useUiContent();
  return (
    <div
      className={`flex flex-shrink-0 items-center gap-1.5 border-t border-black/[0.05] px-2.5 dark:border-white/10 ${
        fill ? 'h-full' : ''
      }`}
      style={fill ? undefined : { height: FOOTER_H }}
    >
      <AffiliateInfoIcon languageCode={languageCode} note={notes} />
      <p
        title={ui.affiliateDisclosure}
        className="line-clamp-2 text-[11px] leading-[1.2] text-zinc-500 dark:text-zinc-400"
      >
        {ui.affiliateDisclosureShort ??
          'We may earn a commission on purchases. It never affects our rankings.'}
      </p>
    </div>
  );
}
